import { createSlice } from "@reduxjs/toolkit";
import { isNotificationSoundMuted } from "../../app/notification/helpers";
import { isTaskNotificationsMuted } from "../../components/organisms/TaskProgress/taskNotificationsMute";

const initialState = {
  dateRangeSelection: { startDate: null, endDate: null, reset: false, key: "" },

  notification: null,

  notificationPreferences: [],

  notifications: [],

  notificationsHasMore: true,

  queryPagination: "",

  settings: { precision: 4, round: false, displayTime: false },

  soundMuted: typeof window !== "undefined" ? isNotificationSoundMuted() : false,

  taskNotificationsMuted:
    typeof window !== "undefined" ? isTaskNotificationsMuted() : false,

  unreadCount: 0,
};

export const miscSlice = createSlice({
  name: "misc",
  initialState,
  reducers: {
    addNotification: (state, action) => {
      const incoming = action.payload;
      if (!incoming?.id) {
        state.notifications = [incoming, ...state.notifications];
        if (incoming && !incoming.is_read) {
          state.unreadCount += 1;
        }
        return;
      }
      const exists = state.notifications.some(item => item.id === incoming.id);
      if (exists) {
        return;
      }
      state.notifications = [incoming, ...state.notifications];
      if (!incoming.is_read) {
        state.unreadCount += 1;
      }
    },
    appendNotifications: (state, action) => {
      const items = Array.isArray(action.payload) ? action.payload : [];
      const seen = new Set(state.notifications.map(item => item.id));
      const next = items.filter(item => item?.id != null && !seen.has(item.id));
      state.notifications = [...state.notifications, ...next];
    },
    markAllAsRead: state => {
      state.notifications = state.notifications.map(notification => ({
        ...notification,
        is_read: true,
      }));
      state.unreadCount = 0;
    },
    resetNotificationPreferences: state => {
      state.notificationPreferences = initialState.notificationPreferences;
    },
    setDateSelectionRange: (state, action) => {
      state.dateRangeSelection = action.payload;
    },
    setDisplayTime: (state, action) => {
      state.settings.displayTime = action.payload;
    },
    resetQueryPagination: (state, action) => {
      state.queryPagination = initialState.queryPagination;
    },
    setNotification: (state, action) => {
      state.notification = action.payload;
    },
    setNotificationPreferences: (state, action) => {
      state.notificationPreferences = action.payload;
    },
    setNotifications: (state, action) => {
      state.notifications = Array.isArray(action.payload) ? action.payload : [];
    },
    setNotificationsHasMore: (state, action) => {
      state.notificationsHasMore = Boolean(action.payload);
    },
    setQueryPagination: (state, action) => {
      state.queryPagination = action.payload;
    },
    setSoundMuted: (state, action) => {
      state.soundMuted = Boolean(action.payload);
    },
    setTaskNotificationsMuted: (state, action) => {
      state.taskNotificationsMuted = Boolean(action.payload);
    },
    setUnreadCount: (state, action) => {
      const value = Number(action.payload);
      state.unreadCount = Number.isFinite(value) && value > 0 ? value : 0;
    },
    updateNotification: (state, action) => {
      const index = state.notifications.findIndex(item => item.id === action.payload.id);
      if (index < 0) {
        return;
      }
      const previous = state.notifications[index];
      const next = { ...previous, ...action.payload };
      state.notifications[index] = next;
      if (previous && previous.is_read === false && next.is_read === true) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    updateNotificationPreferences: (state, action) => {
      const data = action.payload;
      const index = state.notificationPreferences[data.source][data.event].findIndex(
        item => item.id === action.payload.id,
      );
      state.notificationPreferences[data.source][data.event][index] = data;
    },
    updateSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
  },
  extraReducers: builder => {
    // Matched by type string to avoid a circular import with authSlice.
    builder.addCase("auth/logOut", state => {
      state.notifications = initialState.notifications;
      state.notificationsHasMore = initialState.notificationsHasMore;
      state.unreadCount = initialState.unreadCount;
    });
  },
});

export const {
  addNotification,
  appendNotifications,
  markAllAsRead,
  resetNotificationPreferences,
  resetQueryPagination,
  setDateSelectionRange,
  setDisplayTime,
  setNotification,
  setNotificationPreferences,
  setNotifications,
  setNotificationsHasMore,
  setQueryPagination,
  setSoundMuted,
  setTaskNotificationsMuted,
  setUnreadCount,
  updateNotification,
  updateNotificationPreferences,
  updateSettings,
} = miscSlice.actions;

export default miscSlice.reducer;
